import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { badgeClasses } from '@/utils/badge_classes'

export default class extends Controller {
  static targets = [
    "dropzone",
    "fileInput",
    "progressContainer",
    "progressList",
    "resultsContainer",
    "resultsList"
  ]

  static values = {
    uploadUrl: String,
    taxYear: String
  }

  connect() {
    console.log("Smart Upload controller connected")
    this.uploads = new Map() // Track active uploads by filename
    this.consumer = createConsumer() // Create ActionCable consumer
    this.subscribeToChannel()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.consumer) {
      this.consumer.disconnect()
    }
  }

  subscribeToChannel() {
    this.subscription = this.consumer.subscriptions.create(
      { channel: "TaxOrganizers::SmartUploadChannel" },
      {
        connected: () => {
          console.log("Connected to SmartUploadChannel")
        },
        disconnected: () => {
          console.log("Disconnected from SmartUploadChannel")
        },
        received: (data) => {
          console.log("Received data from channel:", data)
          this.handleUploadUpdate(data)
        }
      }
    )
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add('dragover')
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('dragover')
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('dragover')

    const files = Array.from(event.dataTransfer.files)
    this.processFiles(files)
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    this.processFiles(files)
    // Reset the input so the same file can be selected again
    event.target.value = ''
  }

  processFiles(files) {
    if (files.length === 0) {
      return
    }

    if (files.length > 5) {
      alert('You can upload a maximum of 5 files at once.')
      return
    }

    // Show progress container, hide results if any
    this.progressContainerTarget.style.display = 'block'
    this.resultsContainerTarget.style.display = 'none'
    this.progressListTarget.innerHTML = ''
    this.resultsListTarget.innerHTML = ''

    // Upload each file - backend will validate file types
    files.forEach(file => this.uploadFile(file))
  }

  async uploadFile(file) {
    const uploadId = this.generateUploadId()

    // Add to tracking
    this.uploads.set(uploadId, {
      fileName: file.name,
      status: 'uploading',
      progress: 0
    })

    // Create progress card
    this.addProgressCard(uploadId, file.name)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('upload_id', uploadId)

      const response = await fetch(this.uploadUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: formData
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed')
      }

      // Backend will broadcast "queued" status via ActionCable after enqueuing the job
      console.log('Upload successful:', data)
    } catch (error) {
      console.error('Upload error:', error)
      this.updateProgressCard(uploadId, 'error', 0, error.message)
      this.uploads.delete(uploadId)
    }
  }

  handleUploadUpdate(data) {
    const { upload_id, status, file_name, detection_results, error_message } = data

    console.log('Handling upload update:', { upload_id, status })

    switch (status) {
      case 'queued':
        this.updateProgressCard(upload_id, 'queued', 25)
        break
      case 'processing':
        this.updateProgressCard(upload_id, 'processing', 50)
        break
      case 'completed':
        this.updateProgressCard(upload_id, 'completed', 100)
        this.addResultCard(upload_id, file_name, detection_results)
        this.uploads.delete(upload_id)
        // Show results container
        this.resultsContainerTarget.style.display = 'block'
        break
      case 'failed':
        this.updateProgressCard(upload_id, 'error', 0, error_message)
        this.uploads.delete(upload_id)
        break
    }

    // Hide progress container if all uploads are done
    if (this.uploads.size === 0) {
      setTimeout(() => {
        this.progressContainerTarget.style.display = 'none'
      }, 1000)
    }
  }

  addProgressCard(uploadId, fileName) {
    const card = document.createElement('div')
    card.id = `upload-progress-${uploadId}`
    card.className = 'card mb-3'
    card.innerHTML = `
      <div class="card-body">
        <div class="d-flex align-items-center mb-2">
          <i class="fas fa-file-pdf text-danger me-2"></i>
          <span class="flex-grow-1">${this.escapeHtml(fileName)}</span>
          <span class="${badgeClasses({ variant: 'primary' })}" data-status-badge>Uploading</span>
        </div>
        <div class="progress" style="height: 20px;">
          <div class="progress-bar progress-bar-striped progress-bar-animated"
               role="progressbar"
               style="width: 10%"
               data-progress-bar></div>
        </div>
        <div class="mt-2 small text-muted" data-error-message style="display: none;"></div>
      </div>
    `
    this.progressListTarget.appendChild(card)
  }

  updateProgressCard(uploadId, status, progress, errorMessage = null) {
    const card = document.getElementById(`upload-progress-${uploadId}`)
    if (!card) return

    const statusBadge = card.querySelector('[data-status-badge]')
    const progressBar = card.querySelector('[data-progress-bar]')
    const errorDiv = card.querySelector('[data-error-message]')

    // Update progress bar
    progressBar.style.width = `${progress}%`

    // Update status badge
    switch (status) {
      case 'uploading':
        statusBadge.className = badgeClasses({ variant: 'primary' })
        statusBadge.textContent = 'Uploading'
        progressBar.className = 'progress-bar progress-bar-striped progress-bar-animated bg-primary'
        break
      case 'queued':
        statusBadge.className = badgeClasses({ variant: 'secondary' })
        statusBadge.textContent = 'Queued'
        progressBar.className = 'progress-bar progress-bar-striped progress-bar-animated bg-secondary'
        break
      case 'processing':
        statusBadge.className = badgeClasses({ variant: 'info' })
        statusBadge.textContent = 'Processing'
        progressBar.className = 'progress-bar progress-bar-striped progress-bar-animated bg-info'
        break
      case 'completed':
        statusBadge.className = badgeClasses({ variant: 'success' })
        statusBadge.textContent = 'Completed'
        progressBar.className = 'progress-bar bg-success'
        progressBar.classList.remove('progress-bar-animated', 'progress-bar-striped')
        break
      case 'error':
        statusBadge.className = badgeClasses({ variant: 'danger' })
        statusBadge.textContent = 'Error'
        progressBar.className = 'progress-bar bg-danger'
        progressBar.classList.remove('progress-bar-animated', 'progress-bar-striped')
        if (errorMessage) {
          errorDiv.textContent = errorMessage
          errorDiv.style.display = 'block'
        }
        break
    }
  }

  addResultCard(uploadId, fileName, detectionResults) {
    const { forms, year, year_warning } = detectionResults || {}

    const card = document.createElement('div')
    // Change border color if there's a year warning
    card.className = year_warning ? 'card mb-3 border-warning' : 'card mb-3 border-success'

    let formsHtml = ''
    if (forms && forms.length > 0) {
      formsHtml = forms.map((form, index) => {
        const confidencePercent = Math.round(form.confidence * 100)
        const badgeVariant = this.getConfidenceBadgeVariant(confidencePercent)

        return `
          <div class="${index > 0 ? 'mt-2' : ''}">
            <div class="d-flex align-items-center">
              <strong class="me-2">${this.escapeHtml(form.form)}</strong>
              <span class="${badgeClasses({ variant: badgeVariant })}">${confidencePercent}% confident</span>
            </div>
            <small class="text-muted">Found on page ${form.page}</small>
          </div>
        `
      }).join('')
    } else {
      formsHtml = '<p class="text-muted mb-0">No forms detected</p>'
    }

    let yearHtml = ''
    if (year && year.year) {
      const confidenceText = year.method === 'explicit'
        ? 'Explicit match'
        : `${year.hits}/${year.total} occurrences`
      yearHtml = `
        <div class="mt-3">
          <strong>Tax Year:</strong> ${this.escapeHtml(year.year)}
          <span class="${badgeClasses({ variant: 'info', extra: 'ms-2' })}">${confidenceText}</span>
        </div>
      `
    }

    let yearWarningHtml = ''
    if (year_warning) {
      yearWarningHtml = `
        <div class="alert mt-3 mb-0" role="alert" style="background-color: #ff9800; border-color: #f57c00; color: #1a1a1a;">
          <i class="fas fa-exclamation-triangle me-2"></i>
          <strong>Year Mismatch:</strong> ${this.escapeHtml(year_warning)}
        </div>
      `
    }

    card.innerHTML = `
      <div class="card-body">
        <div class="d-flex align-items-start mb-3">
          <i class="fas fa-file-pdf text-danger fa-2x me-3"></i>
          <div class="flex-grow-1">
            <h5 class="mb-1">${this.escapeHtml(fileName)}</h5>
            <small class="text-success">
              <i class="fas fa-check-circle me-1"></i>Uploaded and processed successfully
            </small>
          </div>
        </div>

        <div class="border-top pt-3">
          <h6 class="mb-2">
            <i class="fas fa-search me-2"></i>Detection Results
          </h6>
          ${formsHtml}
          ${yearHtml}
          ${yearWarningHtml}
        </div>
      </div>
    `

    this.resultsListTarget.appendChild(card)
  }

  generateUploadId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  getConfidenceBadgeVariant(confidencePercent) {
    if (confidencePercent >= 80) {
      return 'success'
    } else if (confidencePercent >= 60) {
      return 'warning'
    } else {
      return 'danger'
    }
  }
}
